import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import * as prismicH from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';

import Button from '@/contents/General/Button';
import { RichTextArea } from '@/contents/General/Panels';
import Stack from '@/contents/General/Stack';
import Typography from '@/contents/General/Typography';
import { GlobalContext } from '@/contents/hooks/state';
import Layout from '@/layouts';
import { createClient } from '@/prismicio';
import theme from '@/styles/theme';
import type { NextPageWithLayout } from '@/types';
import { Page } from '@/types/domain';
import type { FaqDocument } from '@/types/prismic.generated';
import DateToString from '@/utils/DateToString';
import getRevalidateSec from '@/utils/getRevalidateSec';

const Container = styled(Box)`
  padding: 0 ${theme.spacing(3)};
`;

type Props = {
  faq: FaqDocument;
  uid: string;
};

const Index: NextPageWithLayout<Props> = ({ faq, uid: path }) => {
  const router = useRouter();
  const { setPage } = useContext(GlobalContext);

  useEffect(() => {
    setPage(Page.FAQ);
  }, []);

  return (
    <Container>
      <Head>
        <title>{`${faq.data.title}｜よくあるご質問《FAQ》 - iihi`}</title>
        <meta
          name="description"
          content={`${faq.data.meta_description ?? ''}`}
        />
        <meta property="og:url" content={`https://iihi.life/faq/${path}`} />
        <meta
          property="og:title"
          content={`${faq.data.title}｜よくあるご質問《FAQ》 - iihi`}
        />
        <meta
          name="og:description"
          content={`${faq.data.meta_description ?? ''}`}
        />
        <meta
          property="og:image"
          content={`${prismicH.asImageSrc(faq.data.og_image) ?? ''}`}
        />
      </Head>
      <Box paddingTop={5} paddingBottom={4}>
        <Typography
          fontSize={21}
          fontWeight={700}
          paddingBottom={2}
          textAlign="center"
          variant="h1"
        >
          {faq.data.title}
        </Typography>
        <Typography
          fontSize={14}
          fontWeight={500}
          color="grey"
          textAlign="center"
        >
          更新日：{DateToString(new Date(faq.last_publication_date))}
        </Typography>
      </Box>
      <Box paddingBottom={3}>
        <RichTextArea>
          <PrismicRichText field={faq.data.content} />
        </RichTextArea>
      </Box>
      <Stack center>
        <Button
          onClick={() => router.back()}
          buttonSize="md"
          color="transparent"
          sx={{ width: '246px' }}
        >
          前のページに戻る
        </Button>
      </Stack>
    </Container>
  );
};

Index.getLayout = (page) => <Layout>{page}</Layout>;

export default Index;

export const getStaticPaths = async () => {
  if (process.env.BUILD_WITH_GENERATION === 'true') {
    const client = createClient();
    const result = await client.getAllByType('faq');
    return {
      paths: result.map((item) => ({ params: { id: item.uid } })),
      fallback: 'blocking',
    };
  }
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export async function getStaticProps({ previewData, params }: any) {
  const uid = params.id;

  const client = createClient({ previewData });

  const faq = await client.getByUID('faq', uid).catch(() => {
    return null;
  });
  if (!faq) return { notFound: true };
  return { props: { faq, uid }, revalidate: getRevalidateSec('long') };
}
